import { useEffect, useState } from 'react';
import { useTenant } from '@/hooks/useTenant';
import { APP_DOMAIN } from '@/lib/domain';

// ─────────────────────────────────────────────────────────────
// Référencement (SEO) et partages sociaux du site d'un commerce.
//
// Le site d'un marchand est servi par la même application que Zifek : sans
// cette synchronisation, un lien partagé (WhatsApp, Facebook, X…) ou un
// moteur de recherche affichait le titre et la description de la PLATEFORME
// au lieu de ceux du commerce.
//
// Ce composant réécrit, dès que le commerce est résolu :
//   • l'URL canonique, en utilisant SON domaine ou SON sous-domaine ;
//   • <title> + meta description, adaptés à la page visitée ;
//   • Open Graph / Twitter (titre, description, image, URL, langue) ;
//   • les données structurées (Store / LocalBusiness) du commerce.
// Sur le site principal Zifek, tout est restauré à l'identique.
// ─────────────────────────────────────────────────────────────

type MetaAttr = 'name' | 'property';

interface SavedMeta {
  attr: MetaAttr;
  key: string;
  content: string | null;
}

const savedRef: { current: SavedMeta[] | null } = { current: null };
const savedCanonical: { current: string | null } = { current: null };

/** Chemins qui ouvrent l'application et non le site public du commerce. */
const APP_PATH_RE = /^\/(admin|client|dashboard|superadmin|developpeur|mon-compte|mon-planning|abonnement|credit|mot-de-passe-oublie|reinitialiser-mot-de-passe)(\/|$)/;

/** Libellé de page affiché dans le titre (SEO) selon le chemin. */
const PAGE_LABELS: Record<string, string> = {
  '/products': 'Produits',
  '/produits': 'Produits',
  '/services': 'Services',
  '/service': 'Services',
  '/cart': 'Panier',
  '/checkout': 'Commande',
  '/booking': 'Réservation',
  '/reservation': 'Réservation',
  '/hebergements': 'Hébergements',
  '/portfolio': 'Portfolio',
  '/partenaires': 'Partenaires',
  '/partners': 'Partenaires',
  '/equipe': 'Équipe',
  '/team': 'Équipe',
  '/search': 'Recherche',
  '/annonces': 'Annonces',
  '/annonce': 'Annonce',
  '/categories': 'Catégories',
  '/vendeurs': 'Vendeurs',
  '/marketplace/produits': 'Marketplace — produits',
  '/marketplace/services': 'Marketplace — services',
  '/page/apropos': 'À propos',
  '/page/contact': 'Contact',
  '/page/blog': 'Blog',
  '/page/faq': 'FAQ',
  '/page/gallery': 'Galerie',
  '/page/galerie': 'Galerie',
  '/page/equipe': 'Équipe',
  '/page/partenaires': 'Partenaires',
  '/page/politique': 'Politique de confidentialité',
  '/page/conditionsdutilisation': 'Conditions d’utilisation',
  '/page/retours': 'Retours',
};

const PAGE_DESCRIPTIONS: Record<string, string> = {
  '/products': 'Découvrez le catalogue, les prix et la disponibilité des produits.',
  '/produits': 'Découvrez le catalogue, les prix et la disponibilité des produits.',
  '/services': 'Découvrez les prestations proposées et réservez en ligne.',
  '/cart': 'Retrouvez les articles de votre panier avant de commander.',
  '/checkout': 'Finalisez votre commande en toute sécurité.',
  '/booking': 'Consultez les créneaux disponibles et réservez votre rendez-vous.',
  '/hebergements': 'Découvrez les hébergements disponibles et réservez votre séjour.',
  '/portfolio': 'Parcourez les réalisations et projets présentés.',
  '/partenaires': 'Découvrez les partenaires du commerce.',
  '/equipe': 'Rencontrez l’équipe du commerce.',
  '/page/apropos': 'En savoir plus sur le commerce, son histoire et son activité.',
  '/page/contact': 'Coordonnées, adresse et moyens de contact.',
};

function saveOriginalMeta(): void {
  if (savedRef.current || typeof document === 'undefined') return;
  const list: SavedMeta[] = [];
  document.head.querySelectorAll('meta[name], meta[property]').forEach((el) => {
    const attr: MetaAttr = el.hasAttribute('name') ? 'name' : 'property';
    const key = el.getAttribute(attr) || '';
    if (!key) return;
    list.push({ attr, key, content: el.getAttribute('content') });
  });
  savedRef.current = list;
  savedCanonical.current = document.head.querySelector('link[rel="canonical"]')?.getAttribute('href') || null;
}

function setMeta(attr: MetaAttr, key: string, content: string): void {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(attr: MetaAttr, key: string): void {
  document.head.querySelectorAll(`meta[${attr}="${key}"]`).forEach((el) => el.remove());
}

function setCanonical(href: string): void {
  let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

function setHreflang(href: string): void {
  let link = document.head.querySelector('link[rel="alternate"][hreflang]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', 'fr');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

/** Adresse absolue de l'image du commerce (les chemins relatifs sont ignorés). */
function absoluteImage(url: string): string {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return '';
}

/** Normalise un chemin pour la recherche de libellé. */
function normalizePath(pathname: string): string {
  return (pathname || '/').replace(/\/+$/, '') || '/';
}

/** Libellé de page, y compris pour les pages dynamiques (fiche produit…). */
function pageLabel(path: string): string {
  if (PAGE_LABELS[path]) return PAGE_LABELS[path];
  if (path.startsWith('/p/')) return 'Page';
  if (path.startsWith('/product/') || path.startsWith('/produit/')) return 'Produit';
  if (path.startsWith('/service/')) return 'Service';
  if (path.startsWith('/hebergement/')) return 'Hébergement';
  if (path.startsWith('/annonce/')) return 'Annonce';
  if (path.startsWith('/boutique/')) return 'Boutique';
  return '';
}

export default function TenantSeoHead() {
  const { isTenant, tenant, customDomain, loading } = useTenant();
  // Le composant est monté au-dessus du routeur : on suit le chemin courant
  // pour que le titre et l'URL canonique correspondent à la page visitée.
  const [path, setPath] = useState(() =>
    typeof window !== 'undefined' ? normalizePath(window.location.pathname) : '/',
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let last = normalizePath(window.location.pathname);
    const sync = () => {
      const next = normalizePath(window.location.pathname);
      if (next !== last) {
        last = next;
        setPath(next);
      }
    };
    const interval = window.setInterval(sync, 400);
    window.addEventListener('popstate', sync);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('popstate', sync);
    };
  }, []);

  useEffect(() => {
    if (loading || typeof document === 'undefined') return;

    const isAppPath = APP_PATH_RE.test(path);

    // Sur les pages de l'application (dashboard, compte…), on ne touche pas
    // au référencement du site public.
    if (isAppPath) return;

    saveOriginalMeta();

    if (!isTenant || !tenant) {
      // Retour sur le site principal : on restaure les métadonnées d'origine.
      const saved = savedRef.current || [];
      saved.forEach((m) => {
        if (m.content) setMeta(m.attr, m.key, m.content);
        else removeMeta(m.attr, m.key);
      });
      const originalTitle = document.head.querySelector('title')?.getAttribute('data-original') || '';
      if (originalTitle) document.title = originalTitle;
      if (savedCanonical.current) setCanonical(savedCanonical.current);
      else document.head.querySelector('link[rel="canonical"]')?.remove();
      document.getElementById('zifek-tenant-ldjson')?.remove();
      return;
    }

    const shopName = tenant.nomcommerce || tenant.name || 'Boutique';
    const place = [tenant.Ville, tenant.Pays].filter(Boolean).join(', ');
    const about = (tenant.aboutus || tenant.description || '').trim();
    const tagline = about || `${shopName}${place ? ` — ${place}` : ''}. Produits, services et réservations en ligne.`;

    const label = pageLabel(path);
    const isHome = path === '/';
    const title = isHome
      ? `${shopName}${tenant.description ? ` — ${tenant.description.slice(0, 45)}` : place ? ` — ${place}` : ''}`
      : `${label || 'Boutique'} · ${shopName}`;

    const baseDescription = PAGE_DESCRIPTIONS[path]
      ? `${shopName} — ${PAGE_DESCRIPTIONS[path]}${place ? ` (${place})` : ''}`
      : tagline;
    const description = baseDescription.replace(/\s+/g, ' ').slice(0, 300);

    // Hôte canonique : le domaine personnalisé du commerce s'il existe,
    // sinon le sous-domaine réellement visité (jamais zifek.fr).
    const host = (customDomain || window.location.hostname).replace(/^https?:\/\//, '').replace(/\/+$/, '');
    const canonicalHost = host || (tenant.user_name ? `${tenant.user_name}.${APP_DOMAIN}` : '');
    const url = canonicalHost ? `https://${canonicalHost}${path === '/' ? '/' : path}` : '';
    const image = absoluteImage(tenant.imageaboutus || tenant.image);

    if (!document.head.querySelector('title')?.getAttribute('data-original')) {
      const titleEl = document.head.querySelector('title');
      if (titleEl) titleEl.setAttribute('data-original', titleEl.textContent || '');
    }
    document.title = title;

    setMeta('name', 'description', description);
    setMeta('property', 'og:site_name', shopName);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:locale', 'fr_FR');
    if (url) {
      setMeta('property', 'og:url', url);
      setCanonical(url);
      setHreflang(url);
    }
    if (image) {
      setMeta('property', 'og:image', image);
      setMeta('property', 'og:image:alt', shopName);
    }
    setMeta('name', 'twitter:card', image ? 'summary_large_image' : 'summary');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    if (image) setMeta('name', 'twitter:image', image);

    // Nettoyage : ces balises n'ont aucun sens sur le site d'un commerce.
    removeMeta('name', 'keywords');
    removeMeta('name', 'geo.region');
    removeMeta('name', 'geo.placename');

    const sameAs = [
      tenant.lienfacebook,
      tenant.lieninstagram,
      tenant.lientiktok,
      tenant.lientwitter,
      tenant.lienlinkedin,
      tenant.lienyoutube,
    ].filter((link) => typeof link === 'string' && /^https?:\/\//i.test(link));

    // Données structurées : on décrit LE commerce, pas la plateforme.
    const ldId = 'zifek-tenant-ldjson';
    let script = document.getElementById(ldId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = ldId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Store',
      '@id': url ? `${url}#store` : undefined,
      name: shopName,
      description: tagline.replace(/\s+/g, ' ').slice(0, 300),
      url: url || undefined,
      ...(image ? { image } : {}),
      ...(tenant.telephone ? { telephone: tenant.telephone } : {}),
      ...(tenant.monaie ? { currenciesAccepted: tenant.monaie } : {}),
      ...(sameAs.length > 0 ? { sameAs } : {}),
      address: {
        '@type': 'PostalAddress',
        ...(tenant.adresse ? { streetAddress: tenant.adresse } : {}),
        ...(tenant.Ville ? { addressLocality: tenant.Ville } : {}),
        ...(tenant.Pays ? { addressCountry: tenant.Pays } : {}),
      },
    });
  }, [isTenant, tenant, customDomain, loading, path]);

  return null;
}